import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Form, Row, Col, ListGroup, Badge, Alert } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const AssignEmployees = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [project, setProject] = useState(null);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAvailableEmployees, setFilteredAvailableEmployees] = useState([]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch project details
        const projectResponse = await fetch(`/api/projects/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!projectResponse.ok) {
          throw new Error('Failed to fetch project details');
        }
        
        const projectData = await projectResponse.json();
        setProject(projectData);
        
        // Fetch assigned employees
        const assignedResponse = await fetch(`/api/employees/project/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          setAssignedEmployees(assignedData);
        }
        
        // Fetch available employees
        const availableResponse = await fetch(`/api/employees/available/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (availableResponse.ok) {
          const availableData = await availableResponse.json();
          setAvailableEmployees(availableData);
          setFilteredAvailableEmployees(availableData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, token]);
  
  // Filter available employees based on search term
  useEffect(() => {
    if (availableEmployees.length > 0 && searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = availableEmployees.filter(
        employee => 
          employee.FirstName.toLowerCase().includes(term) ||
          employee.LastName.toLowerCase().includes(term) ||
          (employee.Email && employee.Email.toLowerCase().includes(term))
      );
      setFilteredAvailableEmployees(filtered);
    } else {
      setFilteredAvailableEmployees(availableEmployees);
    }
  }, [availableEmployees, searchTerm]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prevSelected => {
      if (prevSelected.includes(employeeId)) {
        return prevSelected.filter(id => id !== employeeId);
      } else {
        return [...prevSelected, employeeId];
      }
    });
  };
  
  // Select all available employees
  const selectAllEmployees = () => {
    const allIds = filteredAvailableEmployees.map(emp => emp.EmployeeId);
    setSelectedEmployees(allIds);
  };
  
  // Deselect all employees
  const deselectAllEmployees = () => {
    setSelectedEmployees([]);
  };
  
  // Handle assigning employees to project
  const handleAssignEmployees = async () => {
    if (selectedEmployees.length === 0) {
      toast.info('Please select at least one employee to assign');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`/api/employees/project/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          employeeIds: selectedEmployees
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to assign employees');
      }
      
      const data = await response.json();
      toast.success(`${data.employees.length} employees assigned to project`);
      
      // Navigate back to project detail
      navigate(`/projects/${projectId}`);
    } catch (err) {
      console.error('Error assigning employees:', err);
      toast.error(err.message || 'Failed to assign employees');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle removing an employee from project
  const handleRemoveEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee from the project?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/employees/project/${projectId}/${employeeId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to remove employee');
      }
      
      // Update state
      setAssignedEmployees(prevAssigned => 
        prevAssigned.filter(emp => emp.EmployeeId !== employeeId)
      );
      
      // Fetch available employees again
      const availableResponse = await fetch(`/api/employees/available/${projectId}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableEmployees(availableData);
        setFilteredAvailableEmployees(availableData);
      }
      
      toast.success('Employee removed from project');
    } catch (err) {
      console.error('Error removing employee:', err);
      toast.error(err.message || 'Failed to remove employee');
    }
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }
  
  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
        <div className="mt-3">
          <Button onClick={() => navigate('/projects')} variant="primary">
            Back to Projects
          </Button>
        </div>
      </Alert>
    );
  }
  
  if (!project) {
    return (
      <Alert variant="warning" className="mt-4">
        Project not found
        <div className="mt-3">
          <Button onClick={() => navigate('/projects')} variant="primary">
            Back to Projects
          </Button>
        </div>
      </Alert>
    );
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Assign Employees</h2>
          <h5 className="text-muted">
            Project: <Link to={`/projects/${projectId}`}>{project.ProjectName}</Link>
          </h5>
        </div>
        <div>
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignEmployees}
            disabled={isSaving || selectedEmployees.length === 0}
          >
            {isSaving ? 'Assigning...' : 'Assign Selected Employees'}
          </Button>
        </div>
      </div>
      
      <Row>
        {/* Left column: Currently assigned employees */}
        <Col md={6}>
          <Card className="mb-4 h-100">
            <Card.Header>
              <h5 className="mb-0">Currently Assigned Employees</h5>
            </Card.Header>
            <Card.Body>
              {assignedEmployees.length > 0 ? (
                <ListGroup>
                  {assignedEmployees.map(employee => (
                    <ListGroup.Item 
                      key={employee.EmployeeId}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div>
                          <strong>{employee.FirstName} {employee.LastName}</strong>
                          <Badge 
                            bg="secondary" 
                            className="ms-2"
                          >
                            {formatCurrency(employee.HourlyWage)}/hr
                          </Badge>
                        </div>
                        <small className="text-muted">{employee.Email}</small>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveEmployee(employee.EmployeeId)}
                      >
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center py-4">
                  <p>No employees assigned to this project yet.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Right column: Available employees */}
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Available Employees</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              
              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={selectAllEmployees}
                  disabled={filteredAvailableEmployees.length === 0}
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={deselectAllEmployees}
                  disabled={selectedEmployees.length === 0}
                >
                  Deselect All
                </Button>
              </div>
              
              {filteredAvailableEmployees.length > 0 ? (
                <ListGroup>
                  {filteredAvailableEmployees.map(employee => (
                    <ListGroup.Item 
                      key={employee.EmployeeId}
                      className="d-flex justify-content-between align-items-center"
                      action
                      active={selectedEmployees.includes(employee.EmployeeId)}
                      onClick={() => toggleEmployeeSelection(employee.EmployeeId)}
                    >
                      <div>
                        <div>
                          <strong>{employee.FirstName} {employee.LastName}</strong>
                          <Badge 
                            bg="secondary" 
                            className="ms-2"
                          >
                            {formatCurrency(employee.HourlyWage)}/hr
                          </Badge>
                        </div>
                        <small className="text-muted">{employee.Email}</small>
                      </div>
                      <Form.Check
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.EmployeeId)}
                        onChange={() => {}}
                        onClick={e => e.stopPropagation()}
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center py-4">
                  <p>No available employees found.</p>
                  <Link to="/employees/create">
                    <Button variant="primary">Add New Employee</Button>
                  </Link>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AssignEmployees;
